// Diorama kertas Jawsel: dunia isometrik yang sudah ada diletakkan "datar" di ruang 3D (Three.js) —
// tanah = satu tekstur besar (tebing, rumput, air, jalan, landasan dilukis engine lama), bangunan = kartu sprite
// (paintCode asli) berdiri sedikit miring di tapaknya. Kamera perspektif bebas terjun/dolly → parallax + blur fokus.
// Satuan dunia = piksel iso pada zoom 1 (tile 128x64): grid (gx,gy) → x=(gx-gy)*64, z=(gx+gy)*32, y = ketinggian.
import * as THREE from "three";
import { TILE_W, TILE_H, projectIso } from "../lib/iso";
import { drawCliff, groundTiles } from "../lib/terrain";
import { paintRoads } from "../lib/road";
import { paintRunways } from "../lib/runway";
import { analyzeWater } from "../lib/worldAnalysis";
import { cssCol } from "../lib/color";
import { hashN } from "../lib/sky";
import { createSpriteCache } from "../lib/spriteCache";
import { createIsoOrder } from "../lib/isoOrder";

export const GRID = 60;
const ZG = 0.75;          // zoom lukis tekstur tanah (5760x~3400 px) — tajam untuk pendaratan, muat di GPU perangkat lunak
const MIRING = 18 * Math.PI / 180; // kemiringan kartu bangunan dari tanah (atap terangkat → parallax), tapak tetap menempel
const LAUT = [58, 128, 190];

export const gridKeDunia = (gx, gy, alt = 0) => { const [px, py] = projectIso(gx, gy); return new THREE.Vector3(px, alt, py); };

function teksturKanvas(cv) {
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; t.generateMipmaps = true; t.minFilter = THREE.LinearMipmapLinearFilter;
  return t;
}

// Tanah: lukis seluruh pulau + laut sekeliling ke kanvas (engine lama), jadi plane datar di y=0.
function buatTanah(state, defs) {
  const M = 700; // margin laut (unit dunia)
  const lebar = GRID * TILE_W + 2 * M, tinggi = GRID * TILE_H + M * 1.6;
  const cv = document.createElement("canvas");
  cv.width = Math.round(lebar * ZG); cv.height = Math.round(tinggi * ZG);
  const g = cv.getContext("2d");
  const ox = lebar / 2, oy = M * 0.6;
  const gridToScreen = (gx, gy, z = 0) => { const [px, py] = projectIso(gx, gy, z); return [(ox + px) * ZG, (oy + py) * ZG]; };
  g.fillStyle = cssCol(LAUT); g.fillRect(0, 0, cv.width, cv.height);
  drawCliff(g, gridToScreen, ZG, GRID);
  groundTiles(g, gridToScreen, GRID);
  const waterSet = new Set(state.water || []), roadSet = new Set(state.roads || []);
  const { waterTiles } = analyzeWater(waterSet);
  for (const { x, y } of waterTiles) {
    const T = gridToScreen(x, y), R = gridToScreen(x + 1, y), B = gridToScreen(x + 1, y + 1), L = gridToScreen(x, y + 1);
    g.fillStyle = cssCol([70, 142, 202]); g.beginPath(); g.moveTo(T[0], T[1] - 0.85); g.lineTo(R[0] + 0.85, R[1]); g.lineTo(B[0], B[1] + 0.85); g.lineTo(L[0] - 0.85, L[1]); g.closePath(); g.fill();
  }
  paintRoads(g, roadSet, { gridToScreen, zoom: ZG, hashN, lerpPt: (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t], lamps: [], waterSet });
  const runwaySet = new Set(state.runways || []);
  if (runwaySet.size) paintRunways(g, runwaySet, { gridToScreen, zoom: ZG });
  const geo = new THREE.PlaneGeometry(lebar, tinggi);
  const mesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: teksturKanvas(cv) }));
  mesh.rotation.x = -Math.PI / 2;                         // datar; atas tekstur → -z
  mesh.position.set(0, 0, -oy + tinggi / 2);              // kanvas (0,0) berada di dunia (-ox, 0, -oy)
  mesh.renderOrder = -1;
  void defs;
  return mesh;
}

// Bangunan: kartu sprite paintCode (zoom 1, siang) yang tapaknya menempel tanah dan atapnya terangkat (miring MIRING).
function buatBangunan(state, defs, nightQ) {
  const cache = createSpriteCache({ getDpr: () => 1, getZoom: () => 1, getNightQ: () => nightQ, minZoom: 0.5, maxZoom: 2 });
  const defById = Object.fromEntries((defs || []).map(d => [d.bid, d]));
  const urut = createIsoOrder()(state.buildings || [], defById, defs);
  const grup = new THREE.Group(), tekstur = new Map(), kartu = [];
  urut.forEach((b, i) => {
    const def = defById[b.bid]; if (!def || def.atap) return;
    const sp = cache.bldSprite(b, def, 1, nightQ);
    let tex = tekstur.get(sp.cv); if (!tex) { tex = teksturKanvas(sp.cv); tekstur.set(sp.cv, tex); }
    const [ax, ay] = projectIso(b.x, b.y);
    const kiri = ax + sp.dx, atas = ay + sp.dy, sw = sp.sw, sh = sp.sh;
    const geo = new THREE.PlaneGeometry(sw, sh / Math.cos(MIRING)); geo.translate(0, sh / Math.cos(MIRING) / 2, 0); // poros = tepi bawah
    const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, alphaTest: 0.02 }));
    m.rotation.x = -(Math.PI / 2 - MIRING);
    m.position.set(kiri + sw / 2, 0, atas + sh);
    m.renderOrder = i;
    m.userData = { b, def };
    grup.add(m); kartu.push(m);
  });
  return { grup, kartu };
}

// Awan lembut (sprite) tersebar di ketinggian — kamera menembusnya saat terjun.
export function buatAwan(n, seed, pusat, altMin, altMax) {
  const cv = document.createElement("canvas"); cv.width = cv.height = 256;
  const g = cv.getContext("2d");
  for (const [x, y, r] of [[128, 150, 70], [80, 160, 55], [180, 160, 58], [120, 110, 50], [160, 120, 45]]) {
    const gr = g.createRadialGradient(x, y, 0, x, y, r); gr.addColorStop(0, "rgba(255,255,255,0.95)"); gr.addColorStop(0.65, "rgba(255,255,255,0.6)"); gr.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = gr; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  }
  const tex = teksturKanvas(cv), grup = new THREE.Group();
  for (let i = 0; i < n; i++) {
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, opacity: 0.8 + 0.2 * hashN(seed + i * 7.7), depthWrite: false }));
    const r = 300 + 1600 * hashN(seed + i * 3.1), a = hashN(seed + i * 5.3) * Math.PI * 2;
    s.position.set(pusat.x + Math.cos(a) * r, altMin + (altMax - altMin) * hashN(seed + i * 9.9), pusat.z + Math.sin(a) * r * 0.7);
    const sk = 500 + 900 * hashN(seed + i * 11.3); s.scale.set(sk, sk * 0.6, 1);
    s.renderOrder = 5000 + i;
    grup.add(s);
  }
  return grup;
}

// Kartu billboard dari fungsi gambar kanvas (pesawat, penerjun): gambar(ctx, t) di kanvas lebar×tinggi dengan titik asal (ox,oy).
export function buatAktor(lebar, tinggi, ox, oy, skala) {
  const cv = document.createElement("canvas"); cv.width = lebar; cv.height = tinggi;
  const tex = teksturKanvas(cv); tex.generateMipmaps = false; tex.minFilter = THREE.LinearFilter;
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
  s.scale.set(lebar * skala, tinggi * skala, 1); s.center.set(ox / lebar, 1 - oy / tinggi); s.renderOrder = 9000;
  return { sprite: s, gambar(fn) { const g = cv.getContext("2d"); g.clearRect(0, 0, lebar, tinggi); g.save(); g.translate(ox, oy); fn(g); g.restore(); tex.needsUpdate = true; } };
}

export function buatDunia(state, defs, { malam = 0 } = {}) {
  const scene = new THREE.Scene();
  scene.add(buatTanah(state, defs));
  const { grup, kartu } = buatBangunan(state, defs, malam);
  scene.add(grup);
  return { scene, kartu };
}
